import React, { useState, useLayoutEffect, useEffect, useMemo, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, getApplicantOccupantType, setApplicantOccupantType } from '../context/FormContext';
import { useLocation, useNavigate } from 'react-router-dom';
import FormStepLayout from '../components/FormStepLayout';
import FieldError from '../components/FieldError';
import { PhoneIcon } from '../components/Icons';
import { validateIncidentStepFields } from '../utils/fieldValidation';
import type { FormData, OccupantEntry, OccupantRole } from '../types/form';
import '../styles/theme.css';
import '../styles/form.css';
import '../styles/form-step.css';

const createEmptyOccupant = (id: string, role: OccupantRole = 'passenger'): OccupantEntry => ({
  id,
  name: '',
  role,
  phone_number: '',
  health_insurance: '',
  insurance_company: '',
  insurance_policy_number: '',
  insurance_claim_number: '',
});

/** Pre-filled Applicant row when the filer is the patient (Step 1 patient fields only). */
const buildApplicantRowFromFormData = (fd: FormData): OccupantEntry => {
  const savedRole = getApplicantOccupantType() as OccupantRole;
  const role: OccupantRole =
    savedRole === 'driver' || savedRole === 'passenger' || savedRole === 'pedestrian'
      ? savedRole
      : 'driver';
  const name = [fd.first_name, fd.last_name].filter(Boolean).join(' ').trim();
  const phone_number = (fd.phone_number ?? '').trim();
  return {
    ...createEmptyOccupant('applicant', role),
    name,
    phone_number,
  };
};

/** One empty row so non-patient filers always see Occupant 1 (at least one required). */
const createDefaultOnBehalfOccupantList = (): OccupantEntry[] => [
  createEmptyOccupant(`occupant-${Date.now()}`, 'passenger'),
];

const buildInitialOccupantList = (fd: FormData): OccupantEntry[] => {
  if (fd.occupant_list && fd.occupant_list.length > 0) {
    return fd.occupant_list;
  }
  if (fd.applicant_role === 'on_behalf') {
    return createDefaultOnBehalfOccupantList();
  }
  return [buildApplicantRowFromFormData(fd)];
};

const ASPIRION_PHONE_TEL = '8004984633';

const IncidentForm = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    formData,
    updateFormData,
    ensureDraft,
    persistDraft,
    saveError,
    isSaving,
    draftReady,
    applicationGuid,
  } = useForm();
  const navigate = useNavigate();
  const location = useLocation();

  /** Always hydrate from API (like step 1). If we only ran when `!applicationGuid`, a direct URL hit with a stored id would skip load — `draftReady` stayed false and route guards never ran. */
  useEffect(() => {
    void ensureDraft();
  }, [ensureDraft]);

  const [occupantList, setOccupantList] = useState<OccupantEntry[]>(() =>
    buildInitialOccupantList(formData)
  );

  // Sync from context or Step 1 patient prefill when no saved occupant list.
  useLayoutEffect(() => {
    if (formData.occupant_list.length > 0) {
      setOccupantList(formData.occupant_list);
      return;
    }
    if (formData.applicant_role === 'on_behalf') {
      // One default row comes from useState(buildInitialOccupantList); do not reset here or
      // Step 1 field changes would wipe in-progress occupant data.
      return;
    }
    const seededFirst = buildApplicantRowFromFormData(formData);
    setOccupantList((prev) => {
      if (prev.length <= 1) {
        return [seededFirst];
      }
      return [seededFirst, ...prev.slice(1)];
    });
    // Seed from listed fields only; listing full `formData` would over-sync occupant rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.occupant_list,
    formData.applicant_role,
    formData.first_name,
    formData.last_name,
    formData.phone_number,
  ]);
  const [anotherVehicleInvolved, setAnotherVehicleInvolved] = useState(
    formData.another_vehicle_involved === 'yes' || formData.another_vehicle_involved === 'no'
      ? formData.another_vehicle_involved
      : 'no'
  );
  const [vehicle_insurance_name, setVehicleInsuranceName] = useState(
    formData.vehicle_insurance_name ?? ''
  );
  const [vehicle_policy_number, setVehiclePolicyNumber] = useState(
    formData.vehicle_policy_number ?? ''
  );
  const [vehicle_claim_number, setVehicleClaimNumber] = useState(
    formData.vehicle_claim_number ?? ''
  );

  /**
   * Keep "other vehicle" UI in sync with server `formData` (useState only runs once on mount).
   * Re-run when returning to this step so we do not show "No" after the user saved "Yes" + vehicle data.
   */
  useEffect(() => {
    if (!draftReady || location.pathname !== '/incidents') return;
    const v = formData.another_vehicle_involved;
    if (v === 'yes' || v === 'no') {
      setAnotherVehicleInvolved(v);
    }
    setVehicleInsuranceName(formData.vehicle_insurance_name ?? '');
    setVehiclePolicyNumber(formData.vehicle_policy_number ?? '');
    setVehicleClaimNumber(formData.vehicle_claim_number ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draftReady,
    applicationGuid,
    location.pathname,
    formData.another_vehicle_involved,
    formData.vehicle_insurance_name,
    formData.vehicle_policy_number,
    formData.vehicle_claim_number,
  ]);

  const [faqOpen, setFaqOpen] = useState(false);
  const [focusedOccupantField, setFocusedOccupantField] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const roleOptions = useMemo(
    () =>
      (['driver', 'passenger', 'pedestrian'] as const).map((value) => ({
        value,
        label: t(`forms.step2.roles.${value}`),
      })),
    [t]
  );

  const incidentFaqItems = useMemo(
    () =>
      ([1, 2, 3, 4] as const).map((i) => ({
        question: t(`incidentFaq.q${i}.q`),
        answer: t(`incidentFaq.q${i}.a`),
      })),
    [t]
  );

  const isPatientFiler = formData.applicant_role !== 'on_behalf';

  const incidentFieldErrors = useMemo(
    () =>
      validateIncidentStepFields({
        occupantList,
        anotherVehicleInvolved,
        vehicle_insurance_name,
        vehicle_policy_number,
        vehicle_claim_number,
      }),
    [
      occupantList,
      anotherVehicleInvolved,
      vehicle_insurance_name,
      vehicle_policy_number,
      vehicle_claim_number,
    ]
  );

  const occErr = (index: number, part: 'name' | 'phone'): string | undefined =>
    incidentFieldErrors[`occ-${index}-${part}`];

  const vehErr = (key: 'vehicle-insurance' | 'vehicle-policy' | 'vehicle-claim'): string | undefined =>
    incidentFieldErrors[key];

  useEffect(() => {
    if (!toastMessage) return;
    const id = window.setTimeout(() => setToastMessage(null), 4000);
    return () => window.clearTimeout(id);
  }, [toastMessage]);

  const occupantFieldClass = (
    index: number,
    field: string,
    value: string,
    withIcon = false,
    hasError = false
  ): string => {
    const key = `${index}-${field}`;
    const hasValue = value !== '';
    const focused = focusedOccupantField === key;
    const parts = [
      'form-field',
      hasValue && 'has-value',
      focused && 'focused',
      withIcon && 'has-icon-left',
      hasError && 'form-field--error',
    ];
    return parts.filter(Boolean).join(' ');
  };

  const isDriverTakenByOther = (currentIndex: number): boolean =>
    occupantList.some((o, i) => i !== currentIndex && o.role === 'driver');

  const setRoleForOccupant = (index: number, newRole: OccupantRole): void => {
    if (newRole === 'driver' && isDriverTakenByOther(index)) {
      setToastMessage('validation.occupant.oneDriver');
      return;
    }
    setOccupantList((prev) =>
      prev.map((o, i) => (i === index ? { ...o, role: newRole } : o))
    );
    if (isPatientFiler && index === 0) {
      setApplicantOccupantType(newRole);
    }
  };

  const updateOccupant = (index: number, updates: Partial<OccupantEntry>): void => {
    setOccupantList((prev) =>
      prev.map((o, i) => (i === index ? { ...o, ...updates } : o))
    );
  };

  const addOccupant = (): void => {
    const nextId = `occupant-${Date.now()}`;
    setOccupantList((prev) => [...prev, createEmptyOccupant(nextId, 'passenger')]);
  };

  const removeOccupant = (index: number): void => {
    setOccupantList((prev) => {
      if (isPatientFiler && index === 0) return prev;
      if (!isPatientFiler && prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  /** At most one occupant may be the driver; zero drivers is allowed. */
  const occupantsHaveAtMostOneDriver = (): boolean => {
    return occupantList.filter((o) => o.role === 'driver').length <= 1;
  };

  const nonEmpty = (v: string): boolean => v.trim() !== '';

  /** At least one occupant; every row must have name + phone. */
  const occupantSectionComplete = (): boolean => {
    if (occupantList.length < 1) return false;
    return occupantList.every((o) => nonEmpty(o.name) && nonEmpty(o.phone_number));
  };

  const incidentStepValid =
    Object.keys(incidentFieldErrors).length === 0 &&
    occupantsHaveAtMostOneDriver() &&
    anotherVehicleInvolved !== '' &&
    occupantSectionComplete();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!incidentStepValid) return;
    const next: FormData = {
      ...formData,
      occupant_list: occupantList,
      another_vehicle_involved: anotherVehicleInvolved,
      vehicle_insurance_name: anotherVehicleInvolved === 'yes' ? vehicle_insurance_name : '',
      vehicle_policy_number: anotherVehicleInvolved === 'yes' ? vehicle_policy_number : '',
      vehicle_claim_number: anotherVehicleInvolved === 'yes' ? vehicle_claim_number : '',
    };
    updateFormData(next);
    try {
      await persistDraft(next, 'additional-info');
      navigate('/additional-info');
    } catch {
      /* saveError in context */
    }
  };

  const showVehicleFields = anotherVehicleInvolved === 'yes';

  return (
    <div className="form-step-page">
      {(toastMessage || saveError) && (
        <div role="alert" className="toast toast--error" aria-live="polite">
          {toastMessage ? t(toastMessage) : saveError ? t(saveError) : null}
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <FormStepLayout
          title={t('forms.step2.title')}
          currentStep={2}
          totalSteps={3}
          submitButton
          nextDisabled={!incidentStepValid || isSaving}
          omitPageShell
        >
        <h3 className="form-section-heading">
          {isPatientFiler
            ? t('forms.step2.occupantsHeadingPatient')
            : t('forms.step2.occupantsHeadingOther')}
        </h3>
        {!isPatientFiler && (
          <p className="occupant-instructions">{t('forms.step2.occupantInstructions')}</p>
        )}
        <button type="button" className="btn-add-occupant" onClick={addOccupant}>
          {t('forms.step2.addOccupants')}
        </button>

        {occupantList.map((occupant, index) => {
          const occupantLabel =
            isPatientFiler && index === 0
              ? t('forms.step2.applicant')
              : t('forms.step2.occupantN', { n: isPatientFiler ? index : index + 1 });
          const showRemove = isPatientFiler
            ? index > 0
            : occupantList.length > 1;
          const namePhoneRequired = true;
          const applicantPrefilledLocked = isPatientFiler && index === 0;

          return (
          <div key={occupant.id} className="occupant-row">
            <div className="occupant-row-header">
              <span className="occupant-row-label">
                {occupantLabel}
              </span>
              {showRemove && (
                <button
                  type="button"
                  className="btn-remove-occupant"
                  onClick={() => removeOccupant(index)}
                  aria-label={t('forms.step2.removeAria', { label: occupantLabel })}
                >
                  {t('forms.step2.remove')}
                </button>
              )}
            </div>
            <div className="occupant-row-fields">
              <div
                className={occupantFieldClass(
                  index,
                  'name',
                  occupant.name,
                  false,
                  Boolean(occErr(index, 'name'))
                )}
              >
                <label className="form-label">
                  {t('forms.step2.name')}
                  {namePhoneRequired ? t('forms.step2.requiredSuffix') : ''}
                </label>
                <input
                  id={`occupant-${occupant.id}-name`}
                  type="text"
                  className="form-input"
                  value={occupant.name}
                  onChange={(e) => updateOccupant(index, { name: e.target.value })}
                  onFocus={() => setFocusedOccupantField(`${index}-name`)}
                  onBlur={() => setFocusedOccupantField(null)}
                  aria-invalid={Boolean(occErr(index, 'name'))}
                  aria-describedby={
                    occErr(index, 'name') ? `occupant-${occupant.id}-name-error` : undefined
                  }
                  disabled={applicantPrefilledLocked}
                  title={
                    applicantPrefilledLocked ? t('forms.step2.prefillTitle') : undefined
                  }
                />
                <FieldError
                  id={`occupant-${occupant.id}-name-error`}
                  message={occErr(index, 'name')}
                />
              </div>
              <div className={occupantFieldClass(index, 'role', occupant.role)}>
                <label className="form-label">{t('forms.step2.role')}</label>
                <select
                  id={`occupant-${occupant.id}-role`}
                  className="form-input"
                  value={occupant.role}
                  onChange={(e) =>
                    setRoleForOccupant(index, e.target.value as OccupantRole)
                  }
                  onFocus={() => setFocusedOccupantField(`${index}-role`)}
                  onBlur={() => setFocusedOccupantField(null)}
                  aria-label={t('forms.step2.roleAria')}
                >
                  {roleOptions.map((opt) => {
                    const driverDisabled =
                      opt.value === 'driver' && isDriverTakenByOther(index);
                    return (
                      <option
                        key={opt.value}
                        value={opt.value}
                        disabled={driverDisabled}
                      >
                        {opt.label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div
                className={occupantFieldClass(
                  index,
                  'phone',
                  occupant.phone_number,
                  true,
                  Boolean(occErr(index, 'phone'))
                )}
              >
                <span className="input-icon input-icon-left" aria-hidden>
                  <PhoneIcon />
                </span>
                <label className="form-label">
                  {t('forms.step2.phone')}
                  {namePhoneRequired ? t('forms.step2.requiredSuffix') : ''}
                </label>
                <input
                  id={`occupant-${occupant.id}-phone`}
                  type="tel"
                  className="form-input"
                  value={occupant.phone_number}
                  onChange={(e) => updateOccupant(index, { phone_number: e.target.value })}
                  onFocus={() => setFocusedOccupantField(`${index}-phone`)}
                  onBlur={() => setFocusedOccupantField(null)}
                  aria-invalid={Boolean(occErr(index, 'phone'))}
                  aria-describedby={
                    occErr(index, 'phone') ? `occupant-${occupant.id}-phone-error` : undefined
                  }
                  disabled={applicantPrefilledLocked}
                  title={
                    applicantPrefilledLocked ? t('forms.step2.prefillTitle') : undefined
                  }
                />
                <FieldError
                  id={`occupant-${occupant.id}-phone-error`}
                  message={occErr(index, 'phone')}
                />
              </div>
              <div className={occupantFieldClass(index, 'health', occupant.health_insurance)}>
                <label className="form-label">{t('forms.step2.healthInsurance')}</label>
                <input
                  id={`occupant-${occupant.id}-health`}
                  type="text"
                  className="form-input"
                  value={occupant.health_insurance}
                  onChange={(e) => updateOccupant(index, { health_insurance: e.target.value })}
                  onFocus={() => setFocusedOccupantField(`${index}-health`)}
                  onBlur={() => setFocusedOccupantField(null)}
                />
              </div>
              <div className={occupantFieldClass(index, 'company', occupant.insurance_company)}>
                <label className="form-label">{t('forms.step2.insuranceCompany')}</label>
                <input
                  id={`occupant-${occupant.id}-company`}
                  type="text"
                  className="form-input"
                  value={occupant.insurance_company}
                  onChange={(e) => updateOccupant(index, { insurance_company: e.target.value })}
                  onFocus={() => setFocusedOccupantField(`${index}-company`)}
                  onBlur={() => setFocusedOccupantField(null)}
                />
              </div>
              <div className={occupantFieldClass(index, 'policy', occupant.insurance_policy_number)}>
                <label className="form-label">{t('forms.step2.insurancePolicy')}</label>
                <input
                  id={`occupant-${occupant.id}-policy`}
                  type="text"
                  className="form-input"
                  value={occupant.insurance_policy_number}
                  onChange={(e) =>
                    updateOccupant(index, { insurance_policy_number: e.target.value })
                  }
                  onFocus={() => setFocusedOccupantField(`${index}-policy`)}
                  onBlur={() => setFocusedOccupantField(null)}
                />
              </div>
              <div className={occupantFieldClass(index, 'claim', occupant.insurance_claim_number)}>
                <label className="form-label">{t('forms.step2.insuranceClaim')}</label>
                <input
                  id={`occupant-${occupant.id}-claim`}
                  type="text"
                  className="form-input"
                  value={occupant.insurance_claim_number}
                  onChange={(e) =>
                    updateOccupant(index, { insurance_claim_number: e.target.value })
                  }
                  onFocus={() => setFocusedOccupantField(`${index}-claim`)}
                  onBlur={() => setFocusedOccupantField(null)}
                />
              </div>
            </div>
          </div>
          );
        })}

        <span className="section-label">{t('forms.step2.otherVehicles')}</span>
        <div className="segmented-group">
          <button
            type="button"
            className={`segmented-option ${anotherVehicleInvolved === 'yes' ? 'active' : ''}`}
            onClick={() => setAnotherVehicleInvolved('yes')}
          >
            {t('forms.step2.yes')}
          </button>
          <button
            type="button"
            className={`segmented-option ${anotherVehicleInvolved === 'no' ? 'active' : ''}`}
            onClick={() => setAnotherVehicleInvolved('no')}
          >
            {t('forms.step2.no')}
          </button>
        </div>

        {showVehicleFields && (
          <>
            <h3 className="sub-heading">{t('forms.step2.otherVehicleInsuranceHeading')}</h3>
            <div
              className={`form-field ${vehicle_insurance_name ? 'has-value' : ''} ${
                vehErr('vehicle-insurance') ? 'form-field--error' : ''
              }`}
            >
              <label className="form-label">{t('forms.step2.vehicleInsuranceCompany')}</label>
              <input
                type="text"
                className="form-input"
                value={vehicle_insurance_name}
                onChange={(e) => setVehicleInsuranceName(e.target.value)}
                placeholder={t('forms.step2.placeholderInsuranceCompany')}
                aria-invalid={Boolean(vehErr('vehicle-insurance'))}
              />
              <FieldError message={vehErr('vehicle-insurance')} />
            </div>
            <div
              className={`form-field ${vehicle_policy_number ? 'has-value' : ''} ${
                vehErr('vehicle-policy') ? 'form-field--error' : ''
              }`}
            >
              <label className="form-label">{t('forms.step2.policyNumber')}</label>
              <input
                type="text"
                className="form-input"
                value={vehicle_policy_number}
                onChange={(e) => setVehiclePolicyNumber(e.target.value)}
                placeholder={t('forms.step2.placeholderPolicy')}
                aria-invalid={Boolean(vehErr('vehicle-policy'))}
              />
              <FieldError message={vehErr('vehicle-policy')} />
            </div>
            <div
              className={`form-field ${vehicle_claim_number ? 'has-value' : ''} ${
                vehErr('vehicle-claim') ? 'form-field--error' : ''
              }`}
            >
              <label className="form-label">{t('forms.step2.claimNumber')}</label>
              <input
                type="text"
                className="form-input"
                value={vehicle_claim_number}
                onChange={(e) => setVehicleClaimNumber(e.target.value)}
                placeholder={t('forms.step2.placeholderClaim')}
                aria-invalid={Boolean(vehErr('vehicle-claim'))}
              />
              <FieldError message={vehErr('vehicle-claim')} />
            </div>
          </>
        )}
        </FormStepLayout>
      </form>

      <section className="form-step-faq-outside" aria-label={t('forms.step2.faqAria')}>
        <div className="faq-section">
          <button
            type="button"
            className="faq-toggle"
            onClick={() => setFaqOpen(!faqOpen)}
            aria-expanded={faqOpen}
            aria-controls="incident-faq-panel"
            id="incident-faq-heading"
          >
            {t('forms.step2.faqToggle')}
            <span className="faq-toggle-chevron" aria-hidden>
              {faqOpen ? '▲' : '▼'}
            </span>
          </button>
          {faqOpen && (
            <div
              id="incident-faq-panel"
              className="faq-content"
              role="region"
              aria-labelledby="incident-faq-heading"
            >
              {incidentFaqItems.map((item, i) => (
                <div key={i} className="faq-item">
                  <h3 className="faq-question">{item.question}</h3>
                  <p className="faq-answer">{item.answer}</p>
                </div>
              ))}
              <div className="faq-call-wrap">
                <a
                  href={`tel:${ASPIRION_PHONE_TEL}`}
                  className="btn-faq-call"
                  aria-label={t('forms.step2.faqCallAria')}
                >
                  {t('forms.step2.faqCall')}
                </a>
                <p className="faq-recaptcha">
                  {t('forms.step2.faqRecaptchaIntro')}{' '}
                  <button type="button" className="faq-recaptcha-link">
                    {t('forms.step2.privacyPolicy')}
                  </button>{' '}
                  {t('forms.step2.faqRecaptchaMid')}{' '}
                  <button type="button" className="faq-recaptcha-link">
                    {t('forms.step2.termsOfService')}
                  </button>{' '}
                  {t('forms.step2.faqRecaptchaEnd')}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default IncidentForm;
